/**
 * Faction Panel - displays all 7 factions with their real-time status
 */

import { useGameStore } from '@/state/gameStore';
import { FACTIONS } from '@/game/factions/FactionData';
import type { FactionId } from '@/types';

export interface FactionStatus {
  id: FactionId;
  gold: number;
  unitCount: number;
  buildingCount: number;
  territoryCount: number;
  relationToPlayer: number;
  isAlive: boolean;
}

export class FactionPanel {
  private container: HTMLElement;
  private factionStatuses: Map<FactionId, FactionStatus> = new Map();
  private unsubscribe: (() => void) | null = null;
  private visible: boolean = false;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'faction-panel';
    this.container.innerHTML = this.createHTML();

    document.body.appendChild(this.container);

    // Initialize all factions
    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];
    factionIds.forEach(id => {
      this.factionStatuses.set(id, {
        id,
        gold: 500,
        unitCount: 0,
        buildingCount: 0,
        territoryCount: 0,
        relationToPlayer: 0,
        isAlive: true,
      });
    });

    this.setupEvents();
    this.render();
    this.setupSubscription();
  }

  private createHTML(): string {
    return `
      <style>
        /* ===== Panel Container ===== */
        #faction-panel {
          position: fixed;
          right: 16px;
          top: 16px;
          z-index: 15;
          font-family: 'Noto Serif SC', 'SimSun', serif;
          font-size: 13px;
          line-height: 1.4;
          color: #f5e6d3;
          user-select: none;
          width: 280px;
          max-height: calc(100vh - 100px);
          overflow-y: auto;
          transform: translateX(120%);
          opacity: 0;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        #faction-panel::-webkit-scrollbar {
          width: 6px;
        }

        #faction-panel::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.2);
        }

        #faction-panel::-webkit-scrollbar-thumb {
          background: rgba(139,105,20,0.5);
          border-radius: 3px;
        }

        #faction-panel.visible {
          transform: translateX(0);
          opacity: 1;
        }

        /* ===== Panel Wrapper ===== */
        .fp-wrapper {
          background: linear-gradient(145deg, rgba(74,55,40,0.92) 0%, rgba(45,31,21,0.95) 100%);
          border: 3px solid #8b6914;
          border-radius: 8px;
          box-shadow:
            inset 0 2px 4px rgba(255,220,150,0.15),
            inset 0 -3px 6px rgba(0,0,0,0.4),
            0 6px 20px rgba(0,0,0,0.6);
          overflow: hidden;
          position: relative;
        }

        /* Corner Ornaments */
        .fp-corner {
          position: absolute;
          width: 32px;
          height: 32px;
          background-image: url('/images/ui/decorations/corner-ornaments.png');
          background-size: 200% 200%;
          z-index: 10;
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));
          pointer-events: none;
        }
        .fp-corner.tl { top: -4px; left: -4px; background-position: 0% 0%; }
        .fp-corner.tr { top: -4px; right: -4px; background-position: 100% 0%; }
        .fp-corner.bl { bottom: -4px; left: -4px; background-position: 0% 100%; }
        .fp-corner.br { bottom: -4px; right: -4px; background-position: 100% 100%; }

        /* ===== Header ===== */
        .fp-header {
          background: linear-gradient(180deg, rgba(107,68,35,0.95) 0%, rgba(74,47,23,0.98) 100%);
          border-bottom: 2px solid #8b6914;
          padding: 12px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .fp-title {
          font-size: 16px;
          font-weight: 700;
          color: #ffd35a;
          text-shadow: 0 2px 4px rgba(0,0,0,0.5);
        }

        .fp-close {
          background: none;
          border: none;
          color: #c4a77d;
          cursor: pointer;
          font-size: 20px;
          padding: 0 4px;
          line-height: 1;
          transition: color 0.2s;
        }

        .fp-close:hover {
          color: #ffd35a;
        }

        /* ===== Faction List ===== */
        .fp-list {
          padding: 10px;
        }

        /* ===== Faction Card ===== */
        .fp-card {
          background: linear-gradient(180deg, rgba(92,64,51,0.9) 0%, rgba(61,42,28,0.95) 100%);
          border: 2px solid #6b4423;
          border-radius: 6px;
          margin-bottom: 8px;
          overflow: hidden;
          transition: all 0.15s ease;
        }

        .fp-card:last-child {
          margin-bottom: 0;
        }

        .fp-card {
          cursor: pointer;
        }

        .fp-card:hover {
          border-color: #8b6914;
        }

        .fp-card.is-player {
          border-color: #d4af37;
          box-shadow: 0 0 10px rgba(212,175,55,0.3);
        }

        .fp-card.is-defeated {
          opacity: 0.5;
        }

        .fp-card.is-highlighted {
          border-color: #ffd700;
          box-shadow: 0 0 15px rgba(255,215,0,0.5);
          background: linear-gradient(180deg, rgba(120,90,60,0.95) 0%, rgba(80,55,35,0.98) 100%);
        }

        /* Card Header */
        .fp-card-header {
          display: flex;
          align-items: center;
          padding: 10px 12px;
          gap: 10px;
          border-bottom: 1px solid rgba(139,105,20,0.3);
        }

        .fp-emblem {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          border-radius: 4px;
          overflow: hidden;
        }

        .fp-emblem img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .fp-faction-info {
          flex: 1;
          min-width: 0;
        }

        .fp-faction-name {
          font-size: 15px;
          font-weight: 700;
          color: #f5e6d3;
          margin-bottom: 2px;
        }

        .fp-faction-name .player-tag {
          font-size: 11px;
          color: #ffd35a;
          font-weight: 400;
          margin-left: 6px;
        }

        .fp-relation {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 600;
        }

        .fp-relation.friendly {
          background: linear-gradient(180deg, #2e7d32 0%, #1b5e20 100%);
          color: #c8e6c9;
        }

        .fp-relation.neutral {
          background: linear-gradient(180deg, #5d4037 0%, #3e2723 100%);
          color: #d7ccc8;
        }

        .fp-relation.hostile {
          background: linear-gradient(180deg, #c62828 0%, #8b0000 100%);
          color: #ffcdd2;
        }

        .fp-relation.self {
          background: linear-gradient(180deg, #8b6914 0%, #6b4423 100%);
          color: #ffd35a;
        }

        /* Card Stats */
        .fp-card-stats {
          padding: 8px 12px;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 6px 12px;
        }

        .fp-stat {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
        }

        .fp-stat-icon {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .fp-stat-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .fp-stat-label {
          color: #9e8b6e;
        }

        .fp-stat-value {
          margin-left: auto;
          font-weight: 600;
          color: #f5e6d3;
        }

        .fp-stat-value.gold { color: #ffd700; }
        .fp-stat-value.units { color: #90caf9; }
        .fp-stat-value.buildings { color: #ce93d8; }
        .fp-stat-value.territory { color: #81c784; }

        /* Strength Bar */
        .fp-strength-bar {
          height: 4px;
          background: rgba(0,0,0,0.3);
          border-radius: 2px;
          margin: 6px 12px 10px;
          overflow: hidden;
        }

        .fp-strength-fill {
          height: 100%;
          border-radius: 2px;
          transition: width 0.3s ease;
        }
      </style>

      <div class="fp-wrapper">
        <div class="fp-corner tl"></div>
        <div class="fp-corner tr"></div>
        <div class="fp-corner bl"></div>
        <div class="fp-corner br"></div>

        <div class="fp-header">
          <span class="fp-title">七国势力</span>
          <button class="fp-close">&times;</button>
        </div>

        <div class="fp-list"></div>
      </div>
    `;
  }

  private setupEvents(): void {
    const closeBtn = this.container.querySelector('.fp-close');
    closeBtn?.addEventListener('click', () => this.hide());
  }

  private bindCardClickEvents(): void {
    const cards = this.container.querySelectorAll('.fp-card');
    cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        e.stopPropagation();
        const factionId = card.getAttribute('data-faction') as FactionId;
        if (!factionId) return;

        const currentHighlight = useGameStore.getState().highlightedFaction;
        // Toggle: if same faction clicked, clear highlight; otherwise set new highlight
        const newHighlight = currentHighlight === factionId ? null : factionId;
        useGameStore.getState().setHighlightedFaction(newHighlight);
        // Don't call render() here - let updateFactionStatus handle it
      });
    });
  }

  private setupSubscription(): void {
    let lastHighlightedFaction: FactionId | null = null;
    this.unsubscribe = useGameStore.subscribe((state) => {
      this.visible = state.showFactionPanel;
      this.container.classList.toggle('visible', this.visible);

      // Re-render when highlighted faction changes
      if (state.highlightedFaction !== lastHighlightedFaction) {
        lastHighlightedFaction = state.highlightedFaction;
        if (this.visible) {
          this.render();
        }
      }
    });
  }

  updateFactionStatus(id: FactionId, status: Partial<FactionStatus>): void {
    const current = this.factionStatuses.get(id);
    if (current) {
      // Check if data actually changed to avoid unnecessary renders
      const hasChanged =
        current.gold !== status.gold ||
        current.unitCount !== status.unitCount ||
        current.buildingCount !== status.buildingCount ||
        current.territoryCount !== status.territoryCount ||
        current.relationToPlayer !== status.relationToPlayer ||
        current.isAlive !== status.isAlive;

      if (hasChanged) {
        this.factionStatuses.set(id, { ...current, ...status });
        if (this.visible) {
          this.render();
        }
      }
    }
  }

  private getRelationClass(relation: number, isPlayer: boolean): string {
    if (isPlayer) return 'self';
    if (relation >= 30) return 'friendly';
    if (relation <= -30) return 'hostile';
    return 'neutral';
  }

  private getRelationText(relation: number, isPlayer: boolean): string {
    if (isPlayer) return '本国';
    if (relation >= 60) return '同盟';
    if (relation >= 30) return '友好';
    if (relation <= -60) return '敌对';
    if (relation <= -30) return '冷淡';
    return '中立';
  }

  private render(): void {
    const playerFaction = useGameStore.getState().playerFaction;
    const highlightedFaction = useGameStore.getState().highlightedFaction;
    const listEl = this.container.querySelector('.fp-list');
    if (!listEl) return;

    const factionIds: FactionId[] = ['yan', 'han', 'lin', 'shang', 'xuan', 'tie', 'yun'];

    // Calculate max values for strength bars
    let maxUnits = 1;
    for (const id of factionIds) {
      const status = this.factionStatuses.get(id);
      if (status && status.unitCount > maxUnits) {
        maxUnits = status.unitCount;
      }
    }

    listEl.innerHTML = factionIds.map(id => {
      const faction = FACTIONS[id];
      const status = this.factionStatuses.get(id)!;
      const isPlayer = id === playerFaction;
      const isHighlighted = id === highlightedFaction;
      const colorHex = '#' + faction.color.toString(16).padStart(6, '0');
      const strengthPercent = maxUnits > 0 ? (status.unitCount / maxUnits) * 100 : 0;
      const relationClass = this.getRelationClass(status.relationToPlayer, isPlayer);
      const relationText = this.getRelationText(status.relationToPlayer, isPlayer);

      return `
        <div class="fp-card ${isPlayer ? 'is-player' : ''} ${!status.isAlive ? 'is-defeated' : ''} ${isHighlighted ? 'is-highlighted' : ''}" data-faction="${id}">
          <div class="fp-card-header">
            <div class="fp-emblem">
              <img src="/images/emblems/${id}.webp" alt="${faction.name}" />
            </div>
            <div class="fp-faction-info">
              <div class="fp-faction-name">
                ${faction.name}
                ${isPlayer ? '<span class="player-tag">(你)</span>' : ''}
              </div>
              <span class="fp-relation ${relationClass}">${relationText}</span>
            </div>
          </div>
          <div class="fp-card-stats">
            <div class="fp-stat">
              <span class="fp-stat-icon"><img src="/images/ui/icons/gold.webp" alt="" /></span>
              <span class="fp-stat-label">黄金</span>
              <span class="fp-stat-value gold">${status.gold.toLocaleString()}</span>
            </div>
            <div class="fp-stat">
              <span class="fp-stat-icon"><img src="/images/ui/icons/unit.webp" alt="" /></span>
              <span class="fp-stat-label">单位</span>
              <span class="fp-stat-value units">${status.unitCount}</span>
            </div>
            <div class="fp-stat">
              <span class="fp-stat-icon"><img src="/images/ui/icons/territory.webp" alt="" /></span>
              <span class="fp-stat-label">建筑</span>
              <span class="fp-stat-value buildings">${status.buildingCount}</span>
            </div>
            <div class="fp-stat">
              <span class="fp-stat-icon"><img src="/images/ui/icons/territory.webp" alt="" /></span>
              <span class="fp-stat-label">领地</span>
              <span class="fp-stat-value territory">${status.territoryCount}</span>
            </div>
          </div>
          <div class="fp-strength-bar">
            <div class="fp-strength-fill" style="width: ${strengthPercent}%; background: ${colorHex}"></div>
          </div>
        </div>
      `;
    }).join('');

    // Bind click events after rendering
    this.bindCardClickEvents();
  }

  show(): void {
    useGameStore.getState().setShowFactionPanel(true);
    this.render(); // Re-render when shown
  }

  hide(): void {
    useGameStore.getState().setShowFactionPanel(false);
  }

  toggle(): void {
    const state = useGameStore.getState();
    state.setShowFactionPanel(!state.showFactionPanel);
    if (!state.showFactionPanel) {
      this.render(); // Re-render when showing
    }
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    this.container.remove();
  }
}
